import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { AlertType, HTTPError, IFormModel, ILSystem, IVerknuepfungenResponse } from '@mwe/models';
import { BasicCardComponent, DsgvoDisclaimerComponent, FormComponent, LookAtMeIAmTheFocusNowDirective } from '@mwe/ui/components';
import { ADD_PRODUCTS_NO_BIRTHDATE_ROUTE, ADD_PRODUCTS_ROUTE, ADD_PRODUCTS_SEARCH_ROUTE, ServiceStateEnum } from '@mwe/constants';
import { getProductFromAssociation, isArrayWithMinOneItem } from '@mwe/utils';
import {
  AppStorageService,
  EnvironmentService,
  LoggingService,
  NavigationLogic,
  ProductAddStateService,
  ProductLogic,
  ProductService,
  ProductsWhiteListLogic,
} from '@mwe/services';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
// eslint-disable-next-line
import { InfoboxComponent } from '../../../../../ui/components/src/lib/infobox/infobox.component';
// eslint-disable-next-line
import { InlineNotificationComponent } from '../../../../../ui/components/src/lib/inline-notification/inline-notification.component';

@Component({
  selector: 'mwe-products-search-base',
  templateUrl: './products-search-base.component.html',
  imports: [
    LookAtMeIAmTheFocusNowDirective,
    BasicCardComponent,
    FormComponent,
    DsgvoDisclaimerComponent,
    InfoboxComponent,
    InlineNotificationComponent,
  ],
})
export class ProductsSearchBaseComponent implements OnInit {
  private productAddStateService = inject(ProductAddStateService);
  private productLogic = inject(ProductLogic);
  private appStorage = inject(AppStorageService);
  private productsWhiteListLogic = inject(ProductsWhiteListLogic);
  private loggingService = inject(LoggingService);
  private productService = inject(ProductService);
  private translateService = inject(TranslateService);
  private navigationLogic = inject(NavigationLogic);
  private environmentService = inject(EnvironmentService);
  private router = inject(Router);

  @ViewChild(FormComponent) formRef: FormComponent;
  @Input() formModel: IFormModel;
  @Input() state: ServiceStateEnum;
  @Input() noProductsErrorKey = 'products-add.product-search.noproducts.description';

  messageKeys: string[];
  errorType: AlertType;
  errorTitleKey: string;
  errorMessageKey: string;
  private errorTypeMap: Map<string, AlertType> = new Map();
  private errorTitle: Map<string, string> = new Map();
  private errorDescription: Map<string, string> = new Map();
  private noProductsErrorTitle = 'products-add.product-search.noproducts.title';

  ngOnInit(): void {
    this.errorTitle.set('2004', 'products-add.product-search.notEnoughInformation.title');
    this.errorTypeMap.set('2004', 'info');
    this.errorDescription.set('2004', 'products-add.product-search.notEnoughInformation.description');

    this.errorTitle.set('2003', 'products-add.product-search.associated.title');
    this.errorDescription.set('2003', 'products-add.product-search.associated.description');

    this.errorTitle.set('4431', 'products-add.product-search.businessCustomer.title');
    const route = `${this.navigationLogic.getSwitchPortalUrl()}/${ADD_PRODUCTS_ROUTE}/${ADD_PRODUCTS_SEARCH_ROUTE}`;
    const translationParams = { otherPortal: route };
    const b2BError = this.translateService.instant('products-add.product-search.businessCustomer.description', translationParams);
    this.errorDescription.set('4431', b2BError);
    this.errorTypeMap.set('4431', 'warning');

    this.errorTitle.set('4433', 'products-add.product-search.privateCustomer.title');
    const b2CError = this.translateService.instant('products-add.product-search.privateCustomer.description', translationParams);
    this.errorDescription.set('4433', b2CError);
    this.errorTypeMap.set('4433', 'warning');

    this.errorTitle.set('4432', 'products-add.product-search.wrongCustomerType.title');
    this.errorDescription.set('4432', 'products-add.product-search.wrongCustomerType.description');
    this.errorTypeMap.set('4432', 'warning');

    this.messageKeys = ['products-add.product-search.info'];
    this.handleEmailInvitation();
  }

  validate(): boolean {
    this.formRef?.fireValidation();
    return this.formRef?.form.valid;
  }

  getValues(): any {
    return this.formRef.form.value;
  }

  private handleEmailInvitation() {
    if (this.appStorage.isEmailInvitation()) {
      this.appStorage.removeEmailInvitation();
      this.messageKeys = [
        'products-add.product-search.emailInvitation.title',
        'products-add.product-search.emailInvitation.description',
        ...this.messageKeys,
      ];
    }
  }

  resetError(): void {
    this.errorType = 'danger';
    this.errorTitleKey = null;
    this.errorMessageKey = null;
  }

  handleSearchError(error: HTTPError): void {
    if (error?.code === '9004') {
      this.router.navigateByUrl(`/${ADD_PRODUCTS_ROUTE}/${ADD_PRODUCTS_NO_BIRTHDATE_ROUTE}`);
    } else if (error.is4xxError() && error.isILError()) {
      if (this.errorTitle.has(error.code)) {
        this.errorType = this.errorTypeMap.has(error.code) ? this.errorTypeMap.get(error.code) : 'danger';
        this.errorTitleKey = this.errorTitle.get(error.code);
        this.errorMessageKey = this.errorDescription.get(error.code);
      } else {
        this.errorTitleKey = this.noProductsErrorTitle;
        this.errorMessageKey = this.noProductsErrorKey;
      }
    } else if (error.code === '3001') {
      this.errorTitleKey = this.noProductsErrorTitle;
      this.errorMessageKey = this.noProductsErrorKey;
    } else {
      this.errorTitleKey = 'products.error.title';
      this.errorMessageKey = 'products.error.description';
    }

    this.state = ServiceStateEnum.ERROR;
    throw error;
  }

  handleSearchResponse(response: IVerknuepfungenResponse): Promise<void> {
    let products;
    if (isArrayWithMinOneItem(response?.verknuepfungen)) {
      this.productAddStateService.businessPartnerNumber = response.verknuepfungen[0].geschaeftsPartnerId;
      this.productAddStateService.systemId = response.verknuepfungen[0].systemId as ILSystem;
      this.productAddStateService.associationStatus = response.verknuepfungen[0].verknuepfungsStatus;
      products = getProductFromAssociation(
        response.verknuepfungen[0].produkte,
        response.verknuepfungen[0].geschaeftsPartnerId,
        response.verknuepfungen[0].systemId,
      );
    } else {
      products = [];
    }

    if (!isArrayWithMinOneItem(products)) {
      this.errorTitleKey = 'products-add.product-search.noproducts.title';
      this.errorMessageKey = 'products-add.product-search.noproducts.description';
      this.state = ServiceStateEnum.ERROR;
      return Promise.reject();
    }

    if (this.productsWhiteListLogic.areAllNotInWhiteList(products)) {
      this.errorTitleKey = 'products-add.product-search.productsNotWhiteListed.title';
      this.errorMessageKey = 'products-add.product-search.productsNotWhiteListed.description';
      this.state = ServiceStateEnum.ERROR;
      return Promise.reject();
    }

    this.productAddStateService.addressGroups = this.productLogic.orderAndGroupByAddresses(products);
    this.state = ServiceStateEnum.SUCCESS;
    return Promise.resolve();
  }

  async delTempAssociationIfNecessary(): Promise<void> {
    if (
      this.productAddStateService.businessPartnerNumber &&
      !this.productAddStateService?.associationCompleted &&
      this.productAddStateService?.associationStatus === null &&
      this.productAddStateService.systemId
    ) {
      try {
        await this.productService.deleteTemporaryAssociations(
          this.productAddStateService.businessPartnerNumber,
          this.productAddStateService.systemId,
        );
      } catch (error) {
        this.loggingService.logError(error, `delete temporary association failed`);
      }
    }
  }
}
